
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, TooltipProps } from 'recharts';
import { Eye, Send, CheckSquare, CircleDollarSign } from 'lucide-react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { CaseConversionData } from '@/types/statistics';

interface CaseConversionChartProps {
  data: CaseConversionData;
}

const CaseConversionChart: React.FC<CaseConversionChartProps> = ({ data }) => {
  const chartData = [
    { name: 'Cases Received', value: data.casesReceived, fill: '#8B5CF6', icon: Eye, percent: 100 },
    { name: 'Plans Sent', value: data.plansSent, fill: '#0EA5E9', icon: Send, percent: data.plansSentPercent },
    { name: 'Plans Accepted', value: data.plansAccepted, fill: '#10B981', icon: CheckSquare, percent: data.plansAcceptedPercent },
    { name: 'Cases Completed', value: data.casesCompleted, fill: '#F59E0B', icon: CircleDollarSign, percent: data.casesCompletedPercent }
  ];

  const chartConfig = {
    casesReceived: { label: "Cases Received", color: "#8B5CF6" },
    plansSent: { label: "Plans Sent", color: "#0EA5E9" },
    plansAccepted: { label: "Plans Accepted", color: "#10B981" },
    casesCompleted: { label: "Cases Completed", color: "#F59E0B" },
  };

  return (
    <div className="w-full">
      <div className="space-y-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-[4/2] md:aspect-[3/1] h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 30,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}`}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded"
                            style={{ backgroundColor: data.fill }}
                          />
                          <span className="font-medium">{data.name}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-8">
                          <span>Count: {data.value}</span>
                          <span>Rate: {data.percent}%</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <div className="flex flex-wrap justify-around gap-4">
          {chartData.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex items-center justify-center rounded-full p-1.5" style={{ backgroundColor: `${item.fill}20` }}>
                <item.icon className="h-4 w-4" style={{ color: item.fill }} />
              </div>
              <div>
                <p className="text-sm font-medium">{item.name}</p>
                <p className="text-sm text-muted-foreground">{item.value} ({item.percent}%)</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseConversionChart;
